import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Input } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { SellerSettings, SellerSettingValueMaxLength } from '../../../enums/seller-settings/seller-settings';
import { SellerSettingsOptions } from '../../../enums/seller-settings/seller-settings-options';
import { useGlobalModalLaunchContext } from '../../../contexts/modal-context/global-modal-context';
import { useDdpTermsState } from '../../../hooks/use-ddp-terms-state';
import {
  useBillDutiesAndTaxesToPayor,
  useCreateCustomsItems,
  useCustomsContentType,
  useCustomsItemCountry,
  useCustomsItemDescription,
  useCustomsItemHarmonization,
  useCustomsItemQty,
  useCustomsItemValue,
  useCustomsNonDelivery,
  useCustomsSigner,
  useDefaultExemptionCode,
  useExemptionOrITN,
  useFCMITracking,
  useITNSetting,
} from '../../../hooks/use-seller-setting';
import { getSelectedOptionMessageDescriptor, getSellerSettingOptions } from '../../../services/settings/seller-settings-service';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { updateSellerSettingsBulk } from '../api/save-settings';
import SaveBar from '../components/save-bar';
import commonStyles from '../settings-page.module.less';
import CustomsDeclarations from './customs-declarations';
import { IORSection } from './ior-section';
import ITNOrUSPSDropdownItem from './itn-or-usps-dropdown-item';
import ItnVatUsps from './itn-vat-usps';
import messages from './messages';
import style from './style.module.less';
import TaxIdentifiers from './tax-identifiers';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';

const emptyMessageReplacement = <div style={{ height: '14px' }} />;

// get seller options
const customsContentTypeOptions = getSellerSettingOptions(SellerSettings.customsContentType);
const customsNonDeliveryOptions = getSellerSettingOptions(SellerSettings.customsNonDelivery);
const exemptionOrITNOptions = getSellerSettingOptions(SellerSettings.exemptionOrITN);
const iTNOrUSPSOptions = exemptionOrITNOptions.filter((option) => option.value !== SellerSettingsOptions.vatNumber.value);

const createCustomsItemsOptions = getSellerSettingOptions(SellerSettings.createCustomsItems);

export const InternationalSettings = () => {
  const dispatch = useDispatch();

  const [customsContentType, setCustomsContentType] = useState(useCustomsContentType());
  const [customsNonDelivery, setCustomsNonDelivery] = useState(useCustomsNonDelivery());
  const [fCMITracking, setFCMITrackingTracking] = useState(useFCMITracking());
  const [billDutiesAndTaxesToPayor, setBillDutiesAndTaxesToPayor] = useState(useBillDutiesAndTaxesToPayor());
  const [customsSigner, setCustomsSigner] = useState(useCustomsSigner());

  // Customs
  const [createCustomsItems, setCreateCustomsItems] = useState(useCreateCustomsItems());
  const [customsItemDescription, setCustomsItemDescription] = useState(useCustomsItemDescription());
  const [customsItemQty, setCustomsItemQty] = useState(useCustomsItemQty());
  const [customsItemValue, setCustomsItemValue] = useState(useCustomsItemValue());
  const [customsItemCountry, setCustomsItemCountry] = useState(useCustomsItemCountry());
  const [customsItemHarmonization, setCustomsItemHarmonization] = useState(useCustomsItemHarmonization());

  // ITN/VAT
  const [exemptionOrITN, setExemptionOrITN] = useState(useExemptionOrITN());
  const [iTNTaxIdToDisplay, setITNTaxIdToDisplay] = useAsyncSafeState('');
  const [iTNSetting, setITNSetting] = useState(useITNSetting());
  const [defaultExemptionCode, setDefaultExemptionCode] = useState(useDefaultExemptionCode());
  const { isAcceptanceRequired, isDdpSettingsVisible, refreshTermsState } = useDdpTermsState();
  const { openDdpTermsModal, registerDdpTermsModalClosedCallback } = useGlobalModalLaunchContext();

  // Track DDP setting from Redux for syncing after DDP modal
  const billDutiesFromRedux = useBillDutiesAndTaxesToPayor();
  const [shouldSyncDdpFromRedux, setShouldSyncDdpFromRedux] = useState(false);

  // Sync local state with Redux when DDP modal closes (reactive, no setTimeout needed)
  useEffect(() => {
    if (shouldSyncDdpFromRedux) {
      setBillDutiesAndTaxesToPayor(billDutiesFromRedux);
      setShouldSyncDdpFromRedux(false);
    }
  }, [shouldSyncDdpFromRedux, billDutiesFromRedux]);

  const handleDdpCheckboxChange = useCallback(() => {
    const isEnablingDdp = !billDutiesAndTaxesToPayor;

    // If user is trying to enable DDP and terms not accepted, show modal first
    if (isEnablingDdp && isAcceptanceRequired && openDdpTermsModal) {
      // Register callback to trigger sync after modal closes
      // The modal sets BillDutiesAndTaxesToPayor via API only when user accepts
      // If user cancels, Redux won't be updated, so we safely sync from Redux
      registerDdpTermsModalClosedCallback(async () => {
        await refreshTermsState();
        // Flag to trigger useEffect sync when Redux updates
        setShouldSyncDdpFromRedux(true);
      });
      openDdpTermsModal();
    } else {
      // Normal toggle (either disabling or terms already accepted)
      setBillDutiesAndTaxesToPayor((prev) => !prev);
    }
  }, [billDutiesAndTaxesToPayor, isAcceptanceRequired, openDdpTermsModal, registerDdpTermsModalClosedCallback, refreshTermsState]);

  // oxlint-disable-next-line react-hooks/rules-of-hooks
  const initialState = {
    customsContentType: useCustomsContentType(),
    customsNonDelivery: useCustomsNonDelivery(),
    fCMITracking: useFCMITracking(),
    customsSigner: useCustomsSigner(),
    createCustomsItems: useCreateCustomsItems(),
    customsItemDescription: useCustomsItemDescription(),
    customsItemQty: useCustomsItemQty(),
    customsItemValue: useCustomsItemValue(),
    customsItemCountry: useCustomsItemCountry(),
    customsItemHarmonization: useCustomsItemHarmonization(),
    exemptionOrITN: useExemptionOrITN(),
    iTNSetting: useITNSetting(),
    defaultExemptionCode: useDefaultExemptionCode(),
    billDutiesAndTaxesToPayor: useBillDutiesAndTaxesToPayor(),
  };

  // selected DD option formatted messages
  const getSelectedCustomsContentTypeMessageDescriptor = useCallback(
    () => getSelectedOptionMessageDescriptor(customsContentType, customsContentTypeOptions),
    [customsContentType],
  );
  const getSelectedCustomsNonDeliveryMessageDescriptor = useCallback(
    () => getSelectedOptionMessageDescriptor(customsNonDelivery, customsNonDeliveryOptions),
    [customsNonDelivery],
  );
  const getSelectedExemptionOrITNMessageDescriptor = useCallback(
    () => getSelectedOptionMessageDescriptor(exemptionOrITN, exemptionOrITNOptions),
    [exemptionOrITN],
  );
  const getSelectedCreateCustomsItemsMessageDescriptor = useCallback(
    () => getSelectedOptionMessageDescriptor(createCustomsItems, createCustomsItemsOptions),
    [createCustomsItems],
  );

  const _onSave = useCallback(async () => {
    const statesToCheck = {
      customsContentType,
      customsNonDelivery,
      fCMITracking,
      customsSigner,
      createCustomsItems,
      customsItemDescription,
      customsItemQty,
      customsItemValue,
      customsItemCountry,
      customsItemHarmonization,
      exemptionOrITN,
      iTNSetting,
      defaultExemptionCode,
      billDutiesAndTaxesToPayor,
    };
    const settingsToSave = {};
    Object.keys(statesToCheck).forEach((key) => {
      if (statesToCheck[key] !== initialState[key]) {
        settingsToSave[SellerSettings[key].key] = statesToCheck[key];
      }
    });

    if (iTNTaxIdToDisplay !== '') {
      settingsToSave[SellerSettings.iTNTaxId.key] = iTNTaxIdToDisplay;
    }

    // do save
    if (Object.keys(settingsToSave).length > 0) {
      const newSettings = await updateSellerSettingsBulk(settingsToSave);
      dispatch(saveNewSellerSettings(newSettings));
      setITNTaxIdToDisplay('');
    }
  }, [
    customsContentType,
    customsNonDelivery,
    fCMITracking,
    customsSigner,
    createCustomsItems,
    customsItemDescription,
    customsItemQty,
    customsItemValue,
    customsItemCountry,
    customsItemHarmonization,
    exemptionOrITN,
    iTNSetting,
    defaultExemptionCode,
    billDutiesAndTaxesToPayor,
    iTNTaxIdToDisplay,
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- initialState changes every render
    initialState,
    dispatch,
    setITNTaxIdToDisplay,
  ]);

  const handleSetExemptionOrITN = useCallback(
    (exemptionOrITNOption) => {
      setExemptionOrITN(exemptionOrITNOption);
    },
    [setExemptionOrITN],
  );

  const renderTaxIdentifierDropdown = () => {
    return iTNOrUSPSOptions.map((option) => (
      <ITNOrUSPSDropdownItem
        key={option.value}
        emptyMessageReplacement={emptyMessageReplacement}
        onClick={handleSetExemptionOrITN}
        option={option}
        selected={exemptionOrITN === option.value}
      />
    ));
  };

  const renderTaxIdentifierToggler = () => {
    const messageDescriptor = getSelectedExemptionOrITNMessageDescriptor();
    return !messageDescriptor ? emptyMessageReplacement : <FormattedMessage {...messageDescriptor} />;
  };

  return (
    <React.Fragment>
      <div className={commonStyles.content}>
        <SettingsPageTitle value={messages.header} />
        <Text className={style['page-description']} textType={TextType.Description} value={messages.pageDescription} />
        <Text textType={TextType.H3} value={messages.customs} />
        <div className={style.settings}>
          <div className={style.setting}>
            <div className={style['setting-label']}>
              <FormattedMessage {...SellerSettings.customsContentType.messageDescriptor} />
            </div>
            <div className={style['setting-options']}>
              <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton>
                <DropdownMenuToggler className={style['dropdown-selector']} theme='white'>
                  <FormattedMessage {...getSelectedCustomsContentTypeMessageDescriptor()} />
                </DropdownMenuToggler>
                {customsContentTypeOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setCustomsContentType(option.value)}
                    selected={customsContentType === option.value}
                  >
                    <FormattedMessage {...option.messageDescriptor} />
                  </DropdownMenuItem>
                ))}
              </DropdownMenu>
            </div>
          </div>
          <div className={style.setting}>
            <div className={style['setting-label']}>
              <FormattedMessage {...SellerSettings.customsNonDelivery.messageDescriptor} />
            </div>
            <div className={style['setting-options']}>
              <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton>
                <DropdownMenuToggler className={style['dropdown-selector']} theme='white'>
                  <FormattedMessage {...getSelectedCustomsNonDeliveryMessageDescriptor()} />
                </DropdownMenuToggler>
                {customsNonDeliveryOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setCustomsNonDelivery(option.value)}
                    selected={customsNonDelivery === option.value}
                  >
                    <FormattedMessage {...option.messageDescriptor} />
                  </DropdownMenuItem>
                ))}
              </DropdownMenu>
            </div>
          </div>
          <div className={style.setting}>
            <div className={style['setting-label']}>
              <FormattedMessage {...SellerSettings.createCustomsItems.messageDescriptor} />
            </div>
            <div className={style['setting-options']}>
              <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton>
                <DropdownMenuToggler className={style['dropdown-selector']} theme='white'>
                  <FormattedMessage {...getSelectedCreateCustomsItemsMessageDescriptor()} />
                </DropdownMenuToggler>
                {createCustomsItemsOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setCreateCustomsItems(option.value)}
                    selected={createCustomsItems === option.value}
                  >
                    <FormattedMessage {...option.messageDescriptor} />
                  </DropdownMenuItem>
                ))}
              </DropdownMenu>
            </div>
          </div>
          <CustomsDeclarations
            createCustomsItems={createCustomsItems}
            customsItemCountry={customsItemCountry}
            customsItemDescription={customsItemDescription}
            customsItemHarmonization={customsItemHarmonization}
            customsItemQty={customsItemQty}
            customsItemValue={customsItemValue}
            setCustomsItemCountry={setCustomsItemCountry}
            setCustomsItemDescription={setCustomsItemDescription}
            setCustomsItemHarmonization={setCustomsItemHarmonization}
            setCustomsItemQty={setCustomsItemQty}
            setCustomsItemValue={setCustomsItemValue}
          />
          <div className={style.setting}>
            <div className={style['setting-label']}>
              <FormattedMessage {...SellerSettings.customsSigner.messageDescriptor} />
            </div>
            <Input
              className={classnames(style['setting-options'], style['sign-customs-as-input'])}
              data-testid='international.customs-signer'
              maxlength={SellerSettingValueMaxLength}
              onChange={(value) => setCustomsSigner(value)}
              type='text'
              value={customsSigner}
            />
          </div>
          <div className={style.setting}>
            <div className={style['setting-label']}>
              <FormattedMessage
                {...messages.iTNOrUSPS}
                values={{
                  a: (chunks) => (
                    <a href='https://www.census.gov/foreign-trade/aes/index.html' rel='noreferrer' target='_blank'>
                      {chunks}
                    </a>
                  ),
                }}
              />
            </div>
            <div className={style['setting-options']}>
              <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton>
                <DropdownMenuToggler className={style['dropdown-selector']} theme='white'>
                  {renderTaxIdentifierToggler()}
                </DropdownMenuToggler>
                {renderTaxIdentifierDropdown()}
              </DropdownMenu>
            </div>
          </div>
          <ItnVatUsps
            defaultExemptionCode={defaultExemptionCode}
            exemptionOrITN={exemptionOrITN}
            iTNSetting={iTNSetting}
            iTNTaxId={iTNTaxIdToDisplay}
            setDefaultExemptionCode={setDefaultExemptionCode}
            setITNSetting={setITNSetting}
            setITNTaxId={setITNTaxIdToDisplay}
          />
          <Text textType={TextType.H3} value={messages.taxIdentifiers} />
          <TaxIdentifiers />

          {isDdpSettingsVisible ? (
            <>
              <Text textType={TextType.H3} value={messages.billDutiesAndTaxesToPayor} />
              <FlexRow>
                <label className={style['clickable-label']}>
                  <Checkbox
                    checked={billDutiesAndTaxesToPayor}
                    onChange={handleDdpCheckboxChange}
                    value={billDutiesAndTaxesToPayor?.toString()}
                  />
                  <Text textType={TextType.H4} value={SellerSettings.billDutiesAndTaxesToPayor.messageDescriptor} />
                </label>
              </FlexRow>
              <FlexRow>
                <FlexColumn>
                  <Text
                    className={style['bill-duties-and-taxes']}
                    textType={TextType.Label}
                    value={messages.billDutiesAndTaxesToPayorDescription}
                  />
                </FlexColumn>
              </FlexRow>
            </>
          ) : null}
          <Text textType={TextType.H3} value={messages.iorLabel} />
          <IORSection />
          <Text textType={TextType.H3} value={messages.tracking} />
          <div>
            <Tooltip>
              <TooltipContent>
                <FormattedMessage {...messages.nonTrackableHover} />
              </TooltipContent>
              <div className={style.tooltip}>
                <label className={style['clickable-label']}>
                  <Checkbox
                    checked={fCMITracking}
                    onChange={() => setFCMITrackingTracking((prev) => !prev)}
                    value={fCMITracking?.toString()}
                  />
                  <Text textType={TextType.H4} value={SellerSettings.fCMITracking.messageDescriptor} />
                </label>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>

      <SaveBar onSave={_onSave} />
    </React.Fragment>
  );
};
